import React from 'react';
import { Loading } from '@cotera/client/app/components';
import { PROVIDER_ICONS } from './constants';
import { ConnectedStatusCard } from './credential-connection-cards';
import { ToolConnectionStatusBadge } from './tool-connection-status';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useTeamsBotSettingsModel } from '@cotera/client/v0/settings/teams-bot-settings.model';
import { TeamsBotChannelAgentsSection } from '@cotera/client/v0/settings/teams-bot-channel-agents-section';

const TEAMS_PROVIDER = 'microsoft-teams';

const TeamsIcon =
  PROVIDER_ICONS[TEAMS_PROVIDER as keyof typeof PROVIDER_ICONS] ?? (() => null);

export const TeamsBotConnector: React.FC<{
  title?: string;
  description?: string;
}> = ({
  title = 'Microsoft Teams Bot',
  description = 'Connect Teams so your team can chat with Coco from DMs and channel @mentions.',
}) => {
  const model = useTeamsBotSettingsModel();
  const status = useWatchableValue(model.status);
  const config = useWatchableValue(model.config);
  const isInstalled = config.installed;
  const isLoading = status === 'loading';

  return (
    <div className="space-y-2">
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <TeamsIcon />
            </div>
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>

          {isLoading ? (
            <Loading.Shimmer className="h-8 w-24 rounded-md" />
          ) : isInstalled ? (
            <ToolConnectionStatusBadge label="Connected" />
          ) : null}
        </div>

        {isLoading ? (
          <div className="mt-4 space-y-4">
            <Loading.Shimmer className="h-24 w-full rounded-md" />
            <Loading.Shimmer className="h-32 w-full rounded-md" />
          </div>
        ) : isInstalled ? (
          <div className="mt-4 space-y-4">
            <ConnectedStatusCard
              title="Coco is connected to Teams"
              description={
                config.tenantId !== undefined
                  ? `Connected to tenant ${config.tenantId}. Coco is available in DMs and @mentions.`
                  : 'Your tenant can use Coco from DMs and @mentions.'
              }
              variant="success"
              isActive={true}
            />
            <TeamsBotChannelAgentsSection />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <ol className="list-decimal space-y-1 pl-4 text-xs text-muted-foreground">
              <li>Add the Coco app in Teams.</li>
              <li>
                Click <span className="font-medium">Sign in to Cotera</span>{' '}
                when Coco asks. One sign-in connects your whole workspace.
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};
